/* eslint-disable @typescript-eslint/no-explicit-any -- 
   Protobuf reflection requires dynamic type inspection and runtime type analysis.
   The any type is necessary for accessing protobuf schema metadata and field definitions
   that don't have proper TypeScript types available. */
import { SupportService } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_connect";

import { getEnumValuesDynamic } from "./dynamic-enum-extractor";
import { detectEnumField } from "./enum-utils";
import { logger } from "./logger";

export interface FieldSchema {
  name: string;
  type: "string" | "number" | "boolean" | "enum" | "message" | "repeated";
  isOptional: boolean;
  isRepeated: boolean;
  enumValues?: Array<{ name: string; value: number }>;
  messageFields?: FieldSchema[];
  description?: string;
}

export interface MethodSchema {
  name: string;
  fields: FieldSchema[];
  description?: string;
}

export class ProtobufSchemaExtractor {
  /**
   * Extract schema from protobuf message class using reflection
   */
  static extractMessageSchema(MessageClass: any): FieldSchema[] {
    try {
      // Check for protobuf-es style fields (newer protobuf library)
      if (MessageClass.fields && MessageClass.fields.members) {
        return this.parseProtobufESFields(MessageClass.fields.members);
      }

      // Check for protobuf-es style fields in 'all' property
      if (MessageClass.fields && MessageClass.fields.all) {
        return this.parseProtobufESFields(MessageClass.fields.all);
      }

      // Check for protobuf-es style fields with _fields function
      if (
        MessageClass.fields &&
        typeof MessageClass.fields._fields === "function"
      ) {
        try {
          const fieldsArray = MessageClass.fields._fields();
          return this.parseProtobufESFields(fieldsArray);
        } catch (error) {
          logger.schemaDebug("Failed to call _fields() function", { error });
        }
      }

      // Approach 1: Check if the class has a descriptor property
      if (MessageClass.prototype && MessageClass.prototype.constructor) {
        const descriptor = MessageClass.prototype.constructor.getDescriptor?.();
        if (descriptor) {
          return this.parseDescriptor(descriptor);
        }
      }

      // Approach 2: Check for field definitions (excluding _fields and _normalizer)
      if (MessageClass.fields) {
        const validFields = Object.keys(MessageClass.fields).filter(
          (key) => key !== "_fields" && key !== "_normalizer"
        );
        if (validFields.length > 0) {
          return this.parseFieldDefinitions(MessageClass.fields);
        }
      }

      // Approach 3: Check for _fields property (common in some protobuf implementations)
      if (MessageClass._fields || MessageClass.prototype._fields) {
        const fields = MessageClass._fields || MessageClass.prototype._fields;
        return this.parseFieldDefinitions(fields);
      }

      // Approach 4: Try to get schema from instance
      const instance = new MessageClass();
      if (instance.getType?.()) {
        return this.parseTypeInfo();
      }

      // Approach 5: Use Object.getOwnPropertyDescriptors to analyze the class
      return this.analyzeClassStructure(MessageClass);
    } catch (error) {
      logger.warn(`Failed to extract schema for ${MessageClass.name}`, {
        error,
      });
      return this.fallbackToRuntimeAnalysis(MessageClass);
    }
  }

  private static parseDescriptor(descriptor: any): FieldSchema[] {
    const fields: FieldSchema[] = [];

    if (descriptor.field) {
      for (const fieldDesc of descriptor.field) {
        // In protobuf v3, fields are optional by default unless explicitly marked as required
        // LABEL_REQUIRED = 2, LABEL_OPTIONAL = 1, LABEL_REPEATED = 3
        const isRequired = fieldDesc.label === 2; // LABEL_REQUIRED
        const isRepeated = fieldDesc.label === 3; // LABEL_REPEATED

        fields.push({
          name: fieldDesc.name,
          type: this.mapProtobufTypeToFieldType(fieldDesc.type),
          isOptional: !isRequired, // Fields are optional by default unless marked as required
          isRepeated: isRepeated,
          description: fieldDesc.options?.comment,
        });
      }
    }

    return fields;
  }

  private static parseFieldDefinitions(fields: any): FieldSchema[] {
    const fieldSchemas: FieldSchema[] = [];

    Object.entries(fields).forEach(([fieldName, fieldDef]: [string, any]) => {
      // Fields are optional by default unless explicitly marked as required
      const isRequired = fieldDef.required === true;
      const fieldType = this.mapProtobufTypeToFieldType(
        fieldDef.type || fieldDef
      );

      let enumValues = fieldDef.values || undefined;

      // If this is detected as an enum type but no enum values are provided,
      // try to get them from dynamic extraction
      if (fieldType === "enum" && !enumValues) {
        const dynamicValues = getEnumValuesDynamic(fieldName);
        if (dynamicValues && dynamicValues.length > 0) {
          enumValues = dynamicValues;
        }
      }

      fieldSchemas.push({
        name: fieldName,
        type: fieldType,
        isOptional: !isRequired, // Optional by default
        isRepeated: fieldDef.repeated || false,
        enumValues: enumValues,
        messageFields: fieldDef.messageType
          ? this.extractMessageSchema(fieldDef.messageType)
          : undefined,
      });
    });

    return fieldSchemas;
  }

  /**
   * Parse protobuf-es style field definitions (newer protobuf library)
   */
  private static parseProtobufESFields(fieldsArray: any[]): FieldSchema[] {
    const fieldSchemas: FieldSchema[] = [];

    fieldsArray.forEach((fieldDef: any) => {
      // Fields are optional by default unless explicitly marked as required
      const isRequired = fieldDef.req === true;

      // Convert snake_case field names to camelCase to match instance properties
      const fieldName = fieldDef.localName || fieldDef.name;
      const camelCaseName = this.snakeToCamelCase(fieldName);

      const fieldSchema: FieldSchema = {
        name: camelCaseName,
        type: this.mapProtobufESTypeToFieldType(fieldDef),
        isOptional: !isRequired, // Optional by default
        isRepeated: fieldDef.repeated || false,
        description: fieldDef.comment || undefined,
      };

      // Handle enum fields
      if (
        fieldDef.kind === "enum" &&
        fieldDef.T &&
        typeof fieldDef.T === "object"
      ) {
        fieldSchema.enumValues = this.extractEnumValues(fieldDef.T);
      }

      // Handle message fields
      if (fieldDef.kind === "message" && fieldDef.T) {
        fieldSchema.messageFields = this.extractMessageSchema(fieldDef.T);
      }

      fieldSchemas.push(fieldSchema);
    });

    return fieldSchemas;
  }

  /**
   * Map protobuf-es type to our field type
   */
  private static mapProtobufESTypeToFieldType(
    fieldDef: any
  ): FieldSchema["type"] {
    if (fieldDef.repeated) {
      return "repeated";
    }

    switch (fieldDef.kind) {
      case "scalar":
        // T represents the scalar type number
        switch (fieldDef.T) {
          case 1: // double
          case 2: // float
          case 3: // int64
          case 4: // uint64
          case 5: // int32
          case 13: // uint32
          case 15: // sint32
          case 16: // sint64
          case 17: // fixed32
          case 18: // fixed64
          case 19: // sfixed32
          case 20: // sfixed64
            return "number";
          case 8: // bool
            return "boolean";
          case 9: // string
          case 12: // bytes
            return "string";
          default:
            return "string";
        }
      case "enum":
        return "enum";
      case "message":
        return "message";
      default:
        return "string";
    }
  }

  /**
   * Extract enum values from enum type
   */
  private static extractEnumValues(
    enumType: any
  ): Array<{ name: string; value: number }> {
    const enumValues: Array<{ name: string; value: number }> = [];

    if (enumType.values) {
      Object.entries(enumType.values).forEach(
        ([name, value]: [string, any]) => {
          enumValues.push({
            name,
            value: typeof value === "number" ? value : value.no,
          });
        }
      );
    }

    return enumValues;
  }

  private static parseTypeInfo(): FieldSchema[] {
    // Parse type information if available - placeholder for future implementation
    return [];
  }

  private static analyzeClassStructure(MessageClass: any): FieldSchema[] {
    const fields: FieldSchema[] = [];
    const instance = new MessageClass();

    // Get all enumerable properties from the instance (not static properties)
    const propertyNames = Object.getOwnPropertyNames(instance);

    propertyNames.forEach((propName) => {
      if (
        propName.startsWith("_") ||
        typeof instance[propName] === "function"
      ) {
        return; // Skip private properties and methods
      }

      const value = instance[propName];
      // Use enhanced analysis that can detect enum patterns
      const fieldSchema = this.analyzeFieldForEnumPattern(propName, value);
      fields.push(fieldSchema);
    });

    return fields;
  }

  private static fallbackToRuntimeAnalysis(MessageClass: any): FieldSchema[] {
    // This is your current approach - analyze the instantiated object
    const instance = new MessageClass();
    const fields: FieldSchema[] = [];

    Object.keys(instance).forEach((key) => {
      if (!key.startsWith("_")) {
        const value = instance[key];
        // Use enhanced analysis that can detect enum patterns
        const fieldSchema = this.analyzeFieldForEnumPattern(key, value);
        fields.push(fieldSchema);
      }
    });

    return fields;
  }

  private static mapProtobufTypeToFieldType(pbType: any): FieldSchema["type"] {
    // Map protobuf types to our field types
    const typeMap: { [key: string]: FieldSchema["type"] } = {
      string: "string",
      int32: "number",
      int64: "number",
      uint32: "number",
      uint64: "number",
      double: "number",
      float: "number",
      bool: "boolean",
      enum: "enum",
      message: "message",
    };

    if (typeof pbType === "string") {
      return typeMap[pbType] || "string";
    }

    if (typeof pbType === "number") {
      // Protobuf type constants
      const typeConstants: { [key: number]: FieldSchema["type"] } = {
        1: "number", // double
        2: "number", // float
        3: "number", // int64
        4: "number", // uint64
        5: "number", // int32
        8: "boolean", // bool
        9: "string", // string
        14: "enum", // enum
        11: "message", // message
      };
      return typeConstants[pbType] || "string";
    }

    return "string";
  }

  private static inferTypeFromValue(value: any): FieldSchema["type"] {
    if (Array.isArray(value)) {
      return value.length > 0 ? this.inferTypeFromValue(value[0]) : "string";
    }

    switch (typeof value) {
      case "string":
        return "string";
      case "number":
        return "number";
      case "boolean":
        return "boolean";
      case "object":
        if (value === null) return "string";
        return "message";
      default:
        return "string";
    }
  }

  /**
   * Enhanced field analysis that detects potential enum fields
   */
  private static analyzeFieldForEnumPattern(
    fieldName: string,
    value: any
  ): FieldSchema {
    const baseType = this.inferTypeFromValue(value);

    // Check if this field name suggests it's an enum
    const enumKey = detectEnumField(fieldName);

    if (enumKey && baseType === "number") {
      // This looks like an enum field - try dynamic extraction
      const enumValues = getEnumValuesDynamic(fieldName);

      if (enumValues && enumValues.length > 0) {
        return {
          name: fieldName,
          type: "enum",
          isOptional: true,
          isRepeated: Array.isArray(value),
          enumValues: enumValues,
        };
      }
    }

    // Return regular field schema
    return {
      name: fieldName,
      type: baseType,
      isOptional: true,
      isRepeated: Array.isArray(value),
    };
  }

  /**
   * Extract all method schemas from a specific service
   */
  static extractMethodSchemasForService(
    serviceDefinition: any,
    serviceId: string
  ): MethodSchema[] {
    const methodSchemas: MethodSchema[] = [];

    if (!serviceDefinition?.methods) {
      logger.warn(`Service ${serviceId} has no methods property`);
      return methodSchemas;
    }

    Object.entries(serviceDefinition.methods).forEach(
      ([methodName, method]: [string, any]) => {
        if (method.I) {
          try {
            const fields = this.extractMessageSchema(method.I);
            methodSchemas.push({
              name: methodName,
              fields,
              description: method.description || `${methodName} method`,
            });
          } catch (error) {
            logger.warn(
              `Failed to extract schema for method ${methodName} in service ${serviceId}`,
              { error }
            );
          }
        }
      }
    );

    return methodSchemas;
  }

  /**
   * Extract all method schemas from the support service (legacy method)
   */
  static extractAllMethodSchemas(): MethodSchema[] {
    return this.extractMethodSchemasForService(SupportService, "support");
  }

  /**
   * Convert snake_case to camelCase
   */
  private static snakeToCamelCase(str: string): string {
    return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
  }
}
